/**
 * Web Share helper — invia FILE direttamente al menu nativo del dispositivo
 * (WhatsApp, Mail, Telegram, Drive, AirDrop). Fallback automatico al download
 * se il browser non supporta navigator.share con files.
 *
 * iOS Safari 15+, Chrome Android 89+, Edge mobile: supportato.
 * Desktop: fallback download del file (la maggior parte dei desktop browser
 * non condivide files via API).
 *
 * iter v30 r094 — Path nativo iOS via Capacitor: quando l'app gira dentro
 * Capacitor (TestFlight / App Store) usiamo @capacitor/share + Filesystem
 * per garantire SEMPRE il share sheet nativo iOS con "Salva su Foto" /
 * "Salva su File" / WhatsApp / AirDrop. Senza Capacitor, fallback a
 * navigator.share (web) → blob in nuovo tab (last resort iOS Safari).
 */

import { toast } from "sonner";
import Cookies from "js-cookie";
import { isNative, isIOS } from "@/lib/native";

const isLikelyMobile = () => {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
};

/**
 * iter v30 r096 — Copia un testo negli appunti (best-effort).
 * Usato sui path di fallback (desktop senza share sheet, iOS web in nuovo
 * tab) così l'utente può fare `Ctrl/Cmd+V` nel suo app preferito (WhatsApp
 * Desktop, Mail, Drive, Slack) dopo che il file è stato scaricato.
 * Ritorna `true` se la copia è andata, `false` se i permessi clipboard
 * non sono concessi o il browser non supporta l'API.
 */
async function copyToClipboardSafe(text) {
  if (!text || typeof navigator === "undefined") return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* permessi negati / no-op */ }
  return false;
}

/**
 * iter v30 r094 — Converte un Blob in base64 (string). Necessario per
 * Capacitor Filesystem.writeFile su iOS (NON accetta Blob direttamente).
 * Implementazione "a chunk" per evitare stack overflow su file > 2MB.
 */
async function blobToBase64(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  const chunkSize = 0x8000; // 32KB chunk per evitare argumentsLimit di String.fromCharCode
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

/**
 * iter v30 r094 — Share via Capacitor (iOS/Android app). Scrive i blob su
 * Filesystem (Cache directory, ripulita automaticamente da iOS quando serve
 * spazio) e invoca @capacitor/share con i file URI. Risultato: share sheet
 * iOS nativo con "Salva video" / "Salva immagine" funzionanti al 100%.
 *
 * Lancia eccezione se Capacitor Share fallisce: il chiamante può ripiegare
 * sul path web (navigator.share o download blob).
 */
async function shareViaCapacitor(blobs, files, title, text) {
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const { Share } = await import("@capacitor/share");

  const fileUris = [];
  for (let i = 0; i < blobs.length; i += 1) {
    const { blob } = blobs[i];
    const { filename } = files[i];
    const base64 = await blobToBase64(blob);
    const result = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });
    fileUris.push(result.uri);
  }

  await Share.share({
    title: title || undefined,
    text: text || undefined,
    files: fileUris,
  });
}

/**
 * Aggiunge `?auth=<jwt>` ai URL del backend protetti, se mancante.
 * Il backend /api/files (e /api/live/file, etc.) accetta auth via:
 *   • Authorization: Bearer <token>  header
 *   • ?auth=<token>                  query param  (per <img>, fetch nudo, share links)
 * Questo helper garantisce che fetch(url) funzioni senza dover settare header
 * manualmente — pattern già usato in lib/fotoUrls.js.
 *
 * iter v28 r472 — User M1382: "Salva in galleria" su mobile non faceva
 * nulla perché fetch(url) tornava 401. Root cause: auth mancante.
 */
function withAuthParam(url) {
  if (!url || /[?&]auth=/.test(url)) return url;
  let token = null;
  try { token = Cookies.get("vista_jwt"); } catch { /* no-op */ }
  if (!token) {
    try { token = typeof window !== "undefined" && window.localStorage?.getItem("vista_jwt"); } catch { /* no-op */ }
  }
  if (!token) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}auth=${encodeURIComponent(token)}`;
}

/**
 * Scarica un file blob nel browser come fallback.
 */
function downloadBlob(blob, filename) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1500);
}

/**
 * Fetcha un URL e restituisce {blob, filename}. Append `?auth=<jwt>` se
 * mancante, così funziona anche con endpoints protetti (es. /api/files).
 *
 * iter v30 r093 — Gestione asincrona dei file generati lato server:
 * alcuni endpoint (es. /api/live/before-after-video) renderizzano il file
 * in background e ritornano 202 con `Retry-After` se non ancora pronto.
 * Ripetiamo il fetch fino a `maxRetries` volte rispettando il Retry-After.
 * Cap totale ~50s (sufficiente per render mp4 ~30-40s + buffer).
 */
async function fetchAsBlob(url, filename, { maxRetries = 6 } = {}) {
  let attempt = 0;
  let lastStatus = 0;
  while (attempt <= maxRetries) {
    const res = await fetch(withAuthParam(url), { credentials: "include" });
    lastStatus = res.status;
    if (res.status === 202) {
      // Server sta ancora preparando il file. Rispetta Retry-After se presente.
      const retryAfter = parseInt(res.headers.get("Retry-After") || "8", 10);
      const waitMs = Math.min(Math.max(retryAfter * 1000, 3000), 15000);
      await new Promise((resolve) => setTimeout(resolve, waitMs));
      attempt += 1;
      continue;
    }
    // iter v30 r132 — 503 "Service Unavailable" è il segnale che il job
    // backend è stato esplicitamente marcato come fallito (es. OOM del
    // render). Lo distinguiamo da un generico 5xx così l'utente capisce
    // che deve aspettare qualche minuto invece di "controllare la
    // connessione" (che non c'entra).
    if (res.status === 503) {
      let detail = "Servizio temporaneamente non disponibile";
      try {
        const j = await res.json();
        detail = j?.detail || detail;
      } catch { /* response senza body JSON */ }
      throw new Error(`server_busy: ${detail}`);
    }
    if (!res.ok) throw new Error(`download_failed_${res.status}`);
    const blob = await res.blob();
    return { blob, filename };
  }
  throw new Error(`download_timeout_${lastStatus}`);
}

/**
 * Tenta di condividere uno o più file via menu nativo.
 * Se non supportato: download del file e toast informativo.
 *
 * @param {Object} opts
 * @param {Array<{url:string, filename:string, mime?:string}>} opts.files - lista file
 * @param {string} [opts.title] - titolo del messaggio
 * @param {string} [opts.text] - testo opzionale
 * @returns {Promise<{shared:boolean, downloaded:boolean}>}
 */
export async function shareFiles({ files = [], title = "", text = "" }) {
  if (!files.length) return { shared: false, downloaded: false };

  // iter v30 r098 — Detect video file in the batch: bump maxRetries da 6 a 18
  // così abbiamo ~180s totali per il rendering server. Il backend impiega
  // 30-60s a generare il video MP4 8s e in produzione su container con
  // memory pressure può andare oltre i 90s. Meglio aspettare un po' di più
  // che mostrare "errore" prematuramente all'utente.
  const hasVideo = files.some((f) =>
    (f.mime || "").startsWith("video/") || /\.mp4($|\?)/i.test(f.url || ""),
  );
  const fetchOpts = hasVideo ? { maxRetries: 18 } : undefined;

  // Scarico i blob in parallelo
  let blobs;
  try {
    blobs = await Promise.all(files.map((f) => fetchAsBlob(f.url, f.filename, fetchOpts)));
  } catch (e) {
    // iter v30 r098 — Messaggio più chiaro per l'utente: distingue download
    // fallito (file non disponibile / 4xx) da timeout server (video lento)
    // da server_busy (503 esplicito: job fallito server-side, es. OOM).
    const msg = String(e?.message || "");
    if (msg.startsWith("server_busy")) {
      // 503 esplicito dal backend: il job è andato in errore (OOM/timeout/etc).
      // Detail già contiene il messaggio italiano dell'API.
      const detail = msg.replace(/^server_busy:\s*/, "") || "Server occupato";
      toast.error(detail);
    } else if (msg.includes("timeout")) {
      toast.error(hasVideo
        ? "Video ancora in preparazione · riprova fra 30 secondi"
        : "File ancora in preparazione · riprova fra 10 secondi");
    } else {
      toast.error("Download non riuscito · controlla la connessione e riprova");
    }
    return { shared: false, downloaded: false };
  }

  // iter v30 r094 — Path 1: Capacitor native (iOS/Android app TestFlight).
  // Garantisce SEMPRE il share sheet iOS con "Salva video/immagine" → Foto.
  if (isNative) {
    try {
      await shareViaCapacitor(blobs, files, title, text);
      return { shared: true, downloaded: false, cancelled: false };
    } catch (e) {
      // Su iOS l'utente che annulla lo share sheet manda errore "share canceled"
      const msg = String(e?.message || e || "").toLowerCase();
      if (msg.includes("cancel") || msg.includes("dismiss")) {
        return { shared: false, downloaded: false, cancelled: true };
      }
      // Altro errore Capacitor (es. plugin non installato in vecchie build):
      // proviamo il path web qui sotto.
    }
  }

  // Costruisco oggetti File per Web Share API (richiede File, non Blob)
  const fileObjects = blobs.map(({ blob, filename }, idx) => {
    const mime = files[idx].mime || blob.type || "application/octet-stream";
    return new File([blob], filename, { type: mime });
  });

  // Path 2: Web Share API navigator.share({files})
  const canShareFiles =
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: fileObjects });

  if (canShareFiles && typeof navigator.share === "function") {
    try {
      await navigator.share({
        files: fileObjects,
        title: title || undefined,
        text: text || undefined,
      });
      return { shared: true, downloaded: false, cancelled: false };
    } catch (e) {
      // utente ha annullato o errore — fall back a download solo se non è AbortError
      // iter v29 r221 — User M1547: il toast "Card non generata" appariva
      // anche quando l'utente *annullava* lo share sheet (AbortError).
      // Esponiamo `cancelled:true` così il caller distingue cancel vs errore.
      if (e?.name === "AbortError") return { shared: false, downloaded: false, cancelled: true };
    }
  }

  // Path 3: Fallback — su iOS Safari `<a download>` non funziona per i blob.
  // Apriamo il blob in un nuovo tab: l'utente può fare long-press → "Salva
  // immagine" (o "Salva video" sul player). Su desktop e Android Chrome,
  // <a download> funziona normalmente.
  if (isIOS && !isNative) {
    // iOS web: apri in nuovo tab così l'utente può salvare con long-press
    const objectUrl = URL.createObjectURL(blobs[0].blob);
    try {
      window.open(objectUrl, "_blank");
    } catch {
      window.location.href = objectUrl;
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    // iter v30 r096 — copia il testo descrittivo negli appunti come backup
    const copied = await copyToClipboardSafe(text);
    toast.success(
      copied
        ? "Si è aperto il file. Tieni premuto → 'Salva' per metterlo in Foto. Testo copiato negli appunti."
        : "Si è aperto il file. Tieni premuto → 'Salva immagine' o 'Salva video' per metterlo in Foto."
    );
    return { shared: false, downloaded: true };
  }

  // Path 4: download diretto (desktop browsers, Android Chrome senza share)
  blobs.forEach(({ blob, filename }) => downloadBlob(blob, filename));
  // iter v30 r096 — Sul desktop il share sheet non c'è, quindi il testo
  // descrittivo ("Ecco la card prima e dopo" ecc.) andrebbe perso. Lo
  // copiamo negli appunti così l'utente può fare Ctrl/Cmd+V nel suo
  // WhatsApp Desktop / Mail / Drive subito dopo aver allegato il file.
  const copied = !isLikelyMobile() ? await copyToClipboardSafe(text) : false;
  if (isLikelyMobile()) {
    toast.success(
      blobs.length === 1
        ? "File scaricato — controlla in Download"
        : `${blobs.length} file scaricati — controlla in Download`,
    );
  } else if (copied && text) {
    toast.success(
      blobs.length === 1
        ? "File scaricato. Testo copiato negli appunti (Cmd/Ctrl+V per incollarlo)."
        : `${blobs.length} file scaricati. Testo copiato negli appunti.`,
    );
  } else {
    toast.success(
      blobs.length === 1 ? "File scaricato" : `${blobs.length} file scaricati`,
    );
  }
  return { shared: false, downloaded: true };
}

/**
 * Helper compatto per condividere un singolo PDF (es. brochure).
 */
export async function sharePdf({ url, filename, title = "", text = "" }) {
  return shareFiles({
    files: [{ url, filename, mime: "application/pdf" }],
    title,
    text,
  });
}

/**
 * Helper compatto per condividere un singolo file immagine.
 */
export async function shareImage({ url, filename, title = "", text = "" }) {
  return shareFiles({
    files: [{ url, filename, mime: "image/jpeg" }],
    title,
    text,
  });
}

/**
 * Helper compatto per condividere un singolo file video MP4.
 */
export async function shareVideo({ url, filename, title = "", text = "" }) {
  return shareFiles({
    files: [{ url, filename, mime: "video/mp4" }],
    title,
    text,
  });
}

/**
 * Salva un'immagine SENZA aprire il share sheet — solo download diretto.
 *
 * iter v28 r459 — User M1376: «se clicco SALVA deve solo salvare, non
 * deve aprire il pannello Condividi, soprattutto da desktop».
 *
 * - Desktop: trigger download del file .jpg via blob URL (Chrome/Edge/
 *   Safari salvano nella cartella Downloads).
 * - Mobile: stesso comportamento. Su iOS Safari il file finisce in
 *   "Salva in File" → app File. L'utente può poi spostarlo in Foto.
 *   Per "salva direttamente in Foto" deve usare il pulsante Condividi
 *   (che invece chiama navigator.share() → share sheet con "Salva
 *   immagine").
 */
export async function downloadImage({ url, filename }) {
  try {
    const { blob } = await fetchAsBlob(url, filename);
    downloadBlob(blob, filename);
    toast.success("Foto scaricata");
    return { shared: false, downloaded: true };
  } catch (e) {
    // iter v30 r098 — Fallback estremo: se fetchAsBlob fallisce (CORS,
    // 502, timeout), apriamo direttamente l'URL in nuova tab così
    // l'utente può fare "Salva con nome" manualmente. Meglio che dire
    // solo "Download non riuscito".
    try {
      const a = document.createElement("a");
      a.href = withAuthParam(url);
      a.download = filename;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Foto aperta in nuova scheda · usa 'Salva con nome' (Cmd/Ctrl+S)");
      return { shared: false, downloaded: true };
    } catch {
      toast.error("Download non riuscito · riprova fra qualche secondo");
      return { shared: false, downloaded: false };
    }
  }
}
